import { useState } from 'react';
import { cn } from '../lib/cn';
import { teamGradient } from '../lib/teams';

/**
 * A team/nation badge. Renders the provider crest or national flag when one is
 * available (World Cup, and any league API-Football supplies logos for), and
 * gracefully falls back to the deterministic colour-coded mark if the image is
 * missing or fails to load — so domestic leagues without logos look unchanged.
 */
export function TeamBadge({
  code,
  logo,
  name,
  sizeClass = 'h-7 w-7',
  roundedClass = 'rounded-[8px]',
  textClass = 'text-[9px]',
  fallback = 'seed',
  flag = false,
  className
}: {
  code: string;
  logo?: string | null;
  name?: string;
  /** Tailwind height+width classes for the badge box. */
  sizeClass?: string;
  roundedClass?: string;
  /** Font size class for the fallback code. */
  textClass?: string;
  /** Fallback fill: 'seed' = colourful per-team gradient, 'dark' = neutral dark. */
  fallback?: 'seed' | 'dark';
  /** National flag: fill the tile edge-to-edge (no white plate, no padding) like an
   *  enamel badge. Crests keep the padded white plate so transparent/mono logos read. */
  flag?: boolean;
  /** Extra classes applied to either the image or the fallback box. */
  className?: string;
}) {
  const [errored, setErrored] = useState(false);

  if (logo && !errored) {
    if (flag) {
      // Provider flags are usually square canvases with the nation centred and padded —
      // scale past the edges so the tile reads full-bleed with no letterboxing.
      return (
        <div className={cn('relative shrink-0 overflow-hidden', sizeClass, roundedClass, className)}>
          <img
            src={logo}
            alt={name ?? code}
            loading="lazy"
            onError={() => setErrored(true)}
            className="h-full w-full scale-x-[1.28] scale-y-[1.48] object-cover object-center"
          />
        </div>
      );
    }
    return (
      <img
        src={logo}
        alt={name ?? code}
        loading="lazy"
        onError={() => setErrored(true)}
        className={cn(
          'shrink-0 bg-white object-contain p-[2px]',
          sizeClass,
          roundedClass,
          className
        )}
      />
    );
  }

  const bg = fallback === 'seed' ? teamGradient(code) : 'linear-gradient(140deg,#2A2A33,#0E0E12)';
  return (
    <div
      className={cn(
        'grid shrink-0 place-items-center font-extrabold tabular-nums text-white',
        sizeClass,
        roundedClass,
        textClass,
        className
      )}
      style={{ backgroundImage: bg }}
    >
      {code}
    </div>
  );
}
